import React from 'react';
import { DayOfWeekSelections, DayOfWeek } from 'parent-portal/forms/JobRequestTypes';
import { FormFieldProps } from '../../FormTypes';
import { Grid } from '@material-ui/core';
import { KeyboardTimePicker, MuiPickersUtilsProvider } from '@material-ui/pickers';
import FormField from '../../FormField';
import ErrorText from '../../ErrorText';
import moment from 'moment';
import DateFnsUtils from '@date-io/moment';
import { MaterialUiPickersDate } from '@material-ui/pickers/typings/date';
import { useWidth } from 'reusableComponents/useWidth';
import DayButton from './DayButton';
import { useFieldValidation } from '../../FormHook';
import { checkIfAllDaysHaveTime, checkIfDurationIsValid, checkIfEndTimeIsValid } from './JobTimeValidation';
import { Colors } from 'library';
import { FaClock } from 'react-icons/fa';

interface JobSameTimeProps {
    daysOfTheWeek: FormFieldProps<DayOfWeekSelections>;
}

export default function JobMultiTimePicker({ daysOfTheWeek }: JobSameTimeProps) {
    const { value, setValue } = daysOfTheWeek;

    const width = useWidth();

    useFieldValidation(daysOfTheWeek, checkIfAllDaysHaveTime(value));

    function setTime(day: string, time: MaterialUiPickersDate, type: string) {
        const updatedDay = {
            ...value[day as keyof typeof value],
        };

        if (type === 'start') {
            updatedDay.start = time;
        } else {
            updatedDay.end = time;
        }

        setValue({
            ...value,
            [day as keyof typeof value]: updatedDay,
        });
    }

    const initialFocusedDateStart = moment().startOf('day').hour(12);
    const initialFocusedDateEnd = moment().startOf('day').hour(15);

    const timePicker = ({ day, dayName }: { day: DayOfWeek; dayName: string }) => {
        return (
            <MuiPickersUtilsProvider utils={DateFnsUtils}>
                <Grid container>
                    <Grid
                        item
                        style={{ marginRight: width === 'xs' ? 0 : 30, width: width === 'xs' ? '100%' : undefined }}
                    >
                        <FormField style={{ margin: ['md', 'lg', 'xl'].includes(width) ? 0 : undefined }}>
                            <KeyboardTimePicker
                                id="start"
                                placeholder="Start: 12:00 PM"
                                mask="__:__ _M"
                                value={day.start}
                                onChange={(time) => setTime(dayName, time, 'start')}
                                initialFocusedDate={initialFocusedDateStart}
                                variant="inline"
                                style={{ width: '100%' }}
                                keyboardIcon={<FaClock color={Colors.darkNavy} />}
                            />
                        </FormField>
                    </Grid>
                    <Grid item style={{ width: width === 'xs' ? '100%' : undefined }}>
                        <FormField
                            style={{
                                margin: ['md', 'lg', 'xl'].includes(width) ? 0 : undefined,
                            }}
                        >
                            <KeyboardTimePicker
                                id="end"
                                placeholder="End: 3:00 PM"
                                mask="__:__ _M"
                                value={day.end}
                                onChange={(time) => setTime(dayName, time, 'end')}
                                initialFocusedDate={initialFocusedDateEnd}
                                variant="inline"
                                style={{ width: '100%' }}
                                keyboardIcon={<FaClock color={Colors.darkNavy} />}
                            />
                            {!checkIfEndTimeIsValid(day) && <ErrorText>Invalid end time</ErrorText>}

                            <Grid container xs={12}>
                                {!checkIfDurationIsValid(day) && (
                                    <ErrorText>Duration must be at least 30 minutes</ErrorText>
                                )}
                            </Grid>
                        </FormField>
                    </Grid>
                </Grid>
            </MuiPickersUtilsProvider>
        );
    };

    return (
        <Grid container xs={12} direction="column">
            {Object.entries(value).map(([dayName, day], index) => {
                return day.selected ? (
                    <Grid container style={{ marginBottom: 30, flexWrap: 'nowrap' }} spacing={3}>
                        <Grid item style={{ alignSelf: 'center' }}>
                            <DayButton dayName={dayName} />
                        </Grid>
                        <Grid
                            item
                            style={{
                                flexGrow: 1,
                            }}
                        >
                            {timePicker({ day: day, dayName: dayName })}
                        </Grid>
                    </Grid>
                ) : null;
            })}
        </Grid>
    );
}
